// One-click billing presets — the registry, and the one call behind all of them.
//
// THE MONEY IS NOT HERE, and must not be. Every preset posts a NAME to
// POST /api/billing/shortcut → Xano api 385, which holds what each one charges
// and writes the rows. The browser sends {shortcut, treatment_id} (+ the Tooth
// Taxi options) and nothing else, so a shortcut can never bill an amount of the
// client's choosing. The prices below are for the BUTTON LABEL only — see PRICES.
//
// The day lock is not re-implemented here either: functions/api/billing/shortcut.js
// refuses a closed day, and TreatmentActionBar hides the buttons, both from the
// same rule that governs เพิ่มรายการ Billing.
//
// ── adding a shortcut ────────────────────────────────────────────────────────
// api 385 owns the list. Add the case there first, then one entry here, and the
// action row picks it up. `typeIds` is what the UI reads to know whether the
// visit already carries the preset and what it will cost — keep it in step with
// the endpoint's `case`.
import { postJson, apiDelete } from './api.js'

/**
 * ⚠️ MIRRORS api 385's hard-coded `fee` case, which writes ฿100 — it does NOT
 * come from the catalogue, and the catalogue DISAGREES: billing_type 238 still
 * says price 20, so adding the same fee by hand through เพิ่มรายการ Billing
 * bills ฿20 while this button bills ฿100. The endpoint is authoritative; the
 * catalogue row is what needs fixing. Until it is, this constant is the only
 * honest label, because reading `billing_type.price` would advertise ฿20 and
 * then charge ฿100.
 */
export const FEE_PRICE = 100

export const SHORTCUTS = {
  // ค่าบริการทางการแพทย์ และค่าบริการปลอดเชื้อเครื่องมือแพทย์ — the per-visit
  // sterilisation fee. One per visit; api 385 also refuses a duplicate.
  fee: {
    label: 'ค่าบริการทางการแพทย์',
    typeIds: [238],
    price: FEE_PRICE,
    mode: 'instant',
    once: true,
  },
  // ชุดจัดฟัน — four Dent Pro items in one write: Super Floss, แปรงซอกฟัน,
  // Wax จัดฟัน, แปรงจัดฟัน, each at its catalogue price.
  //
  // NOT `once`, because a patient can genuinely buy a second set — and api 385
  // has no dedupe for it either. That is exactly why it asks first: four rows
  // land per click and the endpoint returns only the last one, so there is no
  // undo to offer afterwards.
  ortho: {
    label: 'ชุดจัดฟัน',
    typeIds: [24, 25, 30, 33],
    mode: 'confirm',
  },
  // Tooth Taxi (mobile clinic) — เคลือบฟลูออไรด์ ฿100/฿200 and/or
  // เคลือบหลุมร่องฟัน ฿500, both claimed to สปสช. The amounts are chosen per
  // patient, so this one opens a picker instead of writing on the click.
  // Offered only on a Tooth Taxi visit (branch 9) — see isToothTaxiTreatment().
  toothtaxi: {
    label: 'Tooth Taxi',
    typeIds: [107, 109],
    mode: 'options',
    toothTaxiOnly: true,
  },
}

// api 385 tests these literally; a third value writes no fluoride line at all.
export const FLUORIDE_OPTIONS = [100, 200]
export const SEALANT_PRICE = 500

// The catalogue rows a preset will bill, in the order api 385 adds them. Used to
// show WHAT a click is about to do — never to price the write.
export const shortcutItems = (types, key) => {
  const ids = SHORTCUTS[key]?.typeIds || []
  const list = Array.isArray(types) ? types : []
  return ids.map((id) => list.find((t) => Number(t?.id) === id)).filter(Boolean)
}

export const shortcutTotal = (types, key) =>
  shortcutItems(types, key).reduce((n, t) => n + (Number(t?.price) || 0), 0)

// Does the visit already carry this preset? Only meaningful for `once` presets.
export const hasShortcutLines = (lines, key) => {
  const ids = new Set(SHORTCUTS[key]?.typeIds || [])
  return (Array.isArray(lines) ? lines : []).some(
    (b) => b?.active !== false && ids.has(Number(b?.billing_type_id)),
  )
}

/**
 * Fire a preset. `extra` carries { tooth_taxi: { fluoride, sealant } } for the
 * Tooth Taxi one and nothing for the others.
 *
 * ⚠️ The response is api 385's `$billing` — the LAST row it wrote, not all of
 * them. `id` is therefore only an undo handle for single-row presets (`fee`);
 * for ortho and toothtaxi it names one of several rows and must be ignored.
 */
export async function runShortcut(key, treatmentId, extra) {
  const res = await postJson('/api/billing/shortcut', {
    shortcut: key,
    treatment_id: Number(treatmentId),
    ...(extra || {}),
  })
  const row = res?.billing ?? res
  const single = (SHORTCUTS[key]?.typeIds || []).length === 1
  return { res, id: single ? Number(row?.id) || 0 : 0 }
}

// ยกเลิก is the ordinary line delete — same endpoint, same audit entry as
// removing it from the table by hand. There is no privileged undo path.
export const undoShortcutLine = (billingId) => apiDelete(`/api/billing/${billingId}`)
